import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from '../../lib/api';

interface Service {
  id: string;
  name: string;
  name_hindi?: string;
  description?: string;
  duration: number;
  price: number;
  category: string;
  image_url?: string;
}

interface SelectedService extends Service {
  quantity?: number;
}

const BookingPage = () => {
  const location = useLocation();
  const preSelectedService = location.state?.selectedService;

  const [selectedServices, setSelectedServices] = useState<SelectedService[]>([]);
  const [subtotal, setSubtotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [showQR, setShowQR] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [paymentSettings, setPaymentSettings] = useState({
    upi_id: '',
    upi_name: '',
    premium_amount: 50,
    qr_code_enabled: false,
    premium_fee_enabled: false,
    whatsapp_number: '',
    whatsapp_enabled: false
  });

  const [pageSettings, setPageSettings] = useState({
    hero_title: 'Book Your Appointment',
    hero_subtitle: 'Schedule your premium grooming experience with our expert team',
    hero_image_url: ''
  });

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    date: new Date().toISOString().split('T')[0], // Current date
    time: '' // Will be selected from available slots
  });

  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState<'upi' | 'visit'>('visit');
  const [isReserving, setIsReserving] = useState(false);

  // Format time from 24h to 12h format
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Load services from API
  useEffect(() => {
    loadServicesFromAPI();
  }, []);

  // Load available slots when date changes
  useEffect(() => {
    if (customerInfo.date) {
      loadAvailableSlots();
    }
  }, [customerInfo.date]);

  // Pre-select service if coming from services page
  useEffect(() => {
    if (preSelectedService && services.length > 0) {
      const serviceExists = services.find(s => s.id === preSelectedService.id);
      if (serviceExists && !selectedServices.find(s => s.id === preSelectedService.id)) {
        addService(serviceExists);
      }
    }
  }, [preSelectedService, services]);

  const loadAvailableSlots = async () => {
    try {
      setLoadingSlots(true);
      const data = await apiClient.getAvailableSlots(customerInfo.date);
      setAvailableSlots(data.availableSlots || []);
    } catch (error: any) {
      console.error('Failed to load available slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const loadServicesFromAPI = async () => {
    try {
      setLoading(true);
      setError(null);

      const [servicesData, categoriesData, paymentData, bookingPageData] = await Promise.all([
        apiClient.getServices(),
        apiClient.getServiceCategories(),
        apiClient.getPaymentSettings(),
        apiClient.getBookingPageSettings()
      ]);

      setServices(servicesData);
      setCategories(['All Services', ...categoriesData.map((cat: any) => cat.name)]);

      // Convert database integers to booleans for payment settings
      setPaymentSettings({
        ...paymentData,
        premium_amount: 50, // force ₹50 even if backend sends 100
        qr_code_enabled: Boolean(paymentData.qr_code_enabled),
        premium_fee_enabled: Boolean(paymentData.premium_fee_enabled),
        whatsapp_enabled: Boolean(paymentData.whatsapp_enabled)
      });

      setPageSettings(bookingPageData);
    } catch (err: any) {
      setError('Failed to load services');
      console.error('Error loading services:', err);

      // Fallback services
      setServices([
        { id: '1', name: 'Machine Hair Cutting', name_hindi: 'मशीन हेयर कटिंग', duration: 30, price: 100, category: 'Hair' },
        { id: '2', name: 'Facial Treatment', name_hindi: 'फेशियल ट्रीटमेंट', duration: 60, price: 400, category: 'Skin Care' },
        { id: '3', name: 'Manicure', name_hindi: 'मैनीक्योर', duration: 30, price: 250, category: 'Nails' }
      ]);
      setCategories(['All Services', 'Hair', 'Skin Care', 'Nails']);
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = services.filter(service =>
    activeCategory === 'all' || activeCategory === 'All Services' ||
    service.category === activeCategory
  );

  const addService = (service: Service) => {
    setSelectedServices(prev => {
      const exists = prev.find(s => s.id === service.id);
      if (exists) {
        return prev.filter(s => s.id !== service.id);
      } else {
        return [...prev, { ...service, quantity: 1 }];
      }
    });
  };

  const updateQuantity = (serviceId: string, quantity: number) => {
    if (quantity < 1) return;
    
    setSelectedServices(prev => 
      prev.map(service => 
        service.id === serviceId 
          ? { ...service, quantity }
          : service
      )
    );
  };

  const removeService = (serviceId: string) => {
    setSelectedServices(prev => prev.filter(service => service.id !== serviceId));
  };

  // Calculate totals whenever selected services change
  useEffect(() => {
    const subtotal = selectedServices.reduce((sum, service) => 
      sum + (service.price * (service.quantity || 1)), 0
    );
    const premium = paymentSettings.premium_fee_enabled ? paymentSettings.premium_amount : 0;
    setSubtotal(subtotal);
    setTotal(subtotal + premium);
  }, [selectedServices, paymentSettings]);

  const generateWhatsAppMessage = () => {
    const servicesList = selectedServices.map(service =>
      `• ${service.name}${service.name_hindi ? ` (${service.name_hindi})` : ''} - ₹${service.price} ${service.quantity && service.quantity > 1 ? `x${service.quantity}` : ''}`
    ).join('\n');

    const message = `🏪 *Salon Booking Request*\n\n` +
      `👤 *Customer Details:*\n` +
      `Name: ${customerInfo.name}\n` +
      `Phone: ${customerInfo.phone}\n` +
      `${customerInfo.email ? `Email: ${customerInfo.email}\n` : ''}` +
      `\n📅 *Appointment Details:*\n` +
      `Date: ${customerInfo.date}\n` +
      `Time: ${customerInfo.time}\n\n` +
      `💅 *Selected Services:*\n${servicesList}\n\n` +
      `💰 *Total Amount: ₹${total}*\n\n` +
      `Please confirm my booking. Thank you!`;

    return encodeURIComponent(message);
  };

  const handleWhatsAppBooking = async () => {
    // Validation checks
    if (selectedServices.length === 0) {
      alert('Please select at least one service to proceed with booking');
      return;
    }

    if (!customerInfo.name.trim()) {
      alert('Please enter your name to proceed with booking');
      return;
    }

    if (!customerInfo.phone.trim()) {
      alert('Please enter your mobile number to proceed with booking');
      return;
    }

    if (!customerInfo.time.trim()) {
      alert('Please select a time slot to proceed with booking');
      return;
    }

    try {
      setIsReserving(true);
      
      // Create booking directly in database
      const bookingData = {
        customerName: customerInfo.name,
        customerPhone: customerInfo.phone,
        customerEmail: customerInfo.email || '',
        bookingDate: customerInfo.date,
        bookingTime: customerInfo.time,
        services: selectedServices.map(s => ({
          serviceId: s.id,
          serviceName: s.name,
          price: s.price,
          quantity: s.quantity || 1
        })),
        totalAmount: total,
        paymentMethod: 'whatsapp',
        paymentStatus: 'pending',
        notes: 'Booking requested via WhatsApp'
      };

      console.log('Creating booking with data:', bookingData);
      const response = await apiClient.createBooking(bookingData);
      console.log('Booking created:', response);
      
      // Generate WhatsApp message and open WhatsApp directly
      const phoneNumber = paymentSettings.whatsapp_number || '917644047648'; // Default number
      const message = generateWhatsAppMessage();
      const whatsappUrl = `https://wa.me/917644047648?text=${message}`;
      window.open(whatsappUrl, '_blank');

      // Reset form after successful booking
      setSelectedServices([]);
      setCustomerInfo({ name: '', phone: '', email: '', date: new Date().toISOString().split('T')[0], time: '' });
      setAvailableSlots([]);
      
      // Refresh available slots
      await loadAvailableSlots();
      
      alert('🎉 Booking confirmed! Your appointment has been scheduled and will appear in admin dashboard.\n\nWhatsApp message sent with your booking details.');
    } catch (error: any) {
      console.error('Booking creation failed:', error);
      console.error('Error details:', error.message, error.stack);
      alert(error.message || 'Failed to create booking. Please try again.');
    } finally {
      setIsReserving(false);
    }
  };

  const generateQRData = () => {
    const upiUrl = `upi://pay?pa=${paymentSettings.upi_id}&pn=${encodeURIComponent(paymentSettings.upi_name)}&am=${total}&cu=INR`;
    return upiUrl;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 pt-20">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading services...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-primary-50 to-primary-100">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {pageSettings.hero_title || 'Book Your Appointment'}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {pageSettings.hero_subtitle || 'Schedule your premium grooming experience with our expert team'}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Services Selection */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Select Services</h2>
                
                {/* Category Filter */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setActiveCategory(category.toLowerCase())}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                        activeCategory === category.toLowerCase() || (activeCategory === 'all' && category === 'All Services')
                          ? 'bg-primary-400 text-black'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>

                {/* Services Grid */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {filteredServices.map((service) => (
                    <div
                      key={service.id}
                      className={`border rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                        selectedServices.find(s => s.id === service.id)
                          ? 'border-primary-400 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => addService(service)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">
                            {service.name}
                            {service.name_hindi && (
                              <span className="block text-sm text-gray-600 font-normal">
                                {service.name_hindi}
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-gray-600">{service.duration} mins</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-primary-400">₹{service.price}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Selected Services */}
                {selectedServices.length > 0 && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Selected Services</h3>
                    <div className="space-y-2">
                      {selectedServices.map((service) => (
                        <div key={service.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">
                              {service.name}
                              {service.name_hindi && (
                                <span className="text-sm text-gray-600 ml-2">({service.name_hindi})</span>
                              )}
                            </p>
                            <p className="text-sm text-gray-600">₹{service.price} × {service.quantity || 1}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateQuantity(service.id, (service.quantity || 1) - 1);
                              }}
                              className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                            >
                              -
                            </button>
                            <span className="w-8 text-center">{service.quantity || 1}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateQuantity(service.id, (service.quantity || 1) + 1);
                              }}
                              className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                            >
                              +
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeService(service.id);
                              }}
                              className="w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 text-red-600 flex items-center justify-center ml-2"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Booking Details */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-24">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Booking Details</h2>
                
                {/* Customer Information */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                    <input
                      type="text"
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
                    <input
                      type="tel"
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      placeholder="Enter your mobile number"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email (Optional)</label>
                    <input
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      placeholder="Enter your email address"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date *</label>
                    <input
                      type="date"
                      value={customerInfo.date}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time *</label>
                    {loadingSlots ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-400 mx-auto mb-2"></div>
                        <p className="text-sm text-gray-600">Loading available slots...</p>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => setCustomerInfo(prev => ({ ...prev, time: slot }))}
                            className={`px-3 py-2 text-sm rounded-lg transition-colors duration-200 ${
                              customerInfo.time === slot
                                ? 'bg-primary-400 text-black font-semibold'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {formatTime(slot)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        <p>No available slots for selected date</p>
                      </div>
                    )}
                    {customerInfo.time && (
                      <p className="text-sm text-primary-600 mt-2 font-medium">
                        Selected: {formatTime(customerInfo.time)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Price Summary */}
                <div className="border-t pt-4 mb-6">
                  <div className="space-y-2">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal</span>
                      <span>₹{subtotal}</span>
                    </div>
                    {paymentSettings.premium_fee_enabled && (
                      <div className="flex justify-between text-gray-600">
                        <span>Premium Fee</span>
                        <span>₹{paymentSettings.premium_amount}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg text-gray-900 pt-2 border-t">
                      <span>Total Amount</span>
                      <span className="text-primary-400">₹{total}</span>
                    </div>
                  </div>
                </div>

                {/* WhatsApp Booking Only */}
<div className="space-y-3 mt-6">
  <button
    onClick={handleWhatsAppBooking}
    disabled={isReserving}
    className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white py-4 rounded-xl font-bold text-lg transition-colors duration-300 flex items-center justify-center gap-2"
  >
    {isReserving ? (
      <>
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
        Reserving Slot...
      </>
    ) : (
      <>
        <i className="ri-whatsapp-line text-xl"></i>
        Book via WhatsApp
      </>
    )}
  </button>
</div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QR Code Modal */}
      {showQR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-qr-code-line text-3xl text-primary-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Complete Payment</h3>
              <p className="text-gray-600">Scan this QR code with any UPI app to complete your payment</p>
            </div>

            <div className="bg-gray-100 rounded-xl p-6 mb-6">
              <div className="w-48 h-48 bg-white rounded-lg mx-auto flex items-center justify-center">
                <i className="ri-qr-code-line text-6xl text-gray-400"></i>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Paying: <span className="font-bold text-primary-400">₹{total}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                UPI ID: {paymentSettings.upi_id}
              </p>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              Scan this QR code with any UPI app like Google Pay, PhonePe, or Paytm to complete your payment
            </p>

            <div className="space-y-3">
              <button
                onClick={() => window.open(generateQRData())}
                className="w-full bg-primary-400 hover:bg-primary-500 text-black py-3 rounded-xl font-semibold transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <i className="ri-smartphone-line"></i>
                Open UPI App
              </button>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(paymentSettings.upi_id);
                    alert('UPI ID copied to clipboard!');
                  }}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-lg font-semibold transition-colors duration-300 text-sm flex items-center justify-center gap-1"
                >
                  <i className="ri-file-copy-line text-sm"></i>
                  Copy UPI ID
                </button>

                {paymentSettings.whatsapp_enabled && (
                  <button
                    onClick={() => {
                      setShowQR(false);
                      handleWhatsAppBooking();
                    }}
                    className="bg-green-100 hover:bg-green-200 text-green-700 py-2 rounded-lg font-semibold transition-colors duration-300 text-sm flex items-center justify-center gap-1"
                  >
                    <i className="ri-whatsapp-line text-sm"></i>
                    WhatsApp
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowQR(false)}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-xl font-semibold transition-colors duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingPage;